import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/client';
import { MaintenanceWindowInput } from '../types';

export function useMaintenanceWindows() {
  return useQuery({
    queryKey: ['maintenance'],
    queryFn: api.getMaintenanceWindows,
    // `active` is computed server-side against the clock, so a recurring
    // window's state changes without any write to invalidate on.
    refetchInterval: 60_000,
  });
}

/** Changing a window changes service statuses too, so refresh those as well. */
function invalidateAfterMaintenance(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['maintenance'] });
  qc.invalidateQueries({ queryKey: ['services'] });
  qc.invalidateQueries({ queryKey: ['service'] });
}

export function useCreateMaintenanceWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MaintenanceWindowInput) => api.createMaintenanceWindow(data),
    onSuccess: () => invalidateAfterMaintenance(qc),
  });
}

export function useUpdateMaintenanceWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MaintenanceWindowInput }) =>
      api.updateMaintenanceWindow(id, data),
    onSuccess: () => invalidateAfterMaintenance(qc),
  });
}

export function useDeleteMaintenanceWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteMaintenanceWindow(id),
    onSuccess: () => invalidateAfterMaintenance(qc),
  });
}
